import React, { useState, useEffect } from 'react';
import { StorefrontCatalogView, type StorefrontItem, formatMinorUnits } from '..';
import { ShoppingBag, CheckCircle, ShieldCheck, X, Loader2 } from 'lucide-react';
import { storefrontApi } from '../api';

export function StorefrontPage(): React.JSX.Element {
  const [items, setItems] = useState<StorefrontItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [checkoutItem, setCheckoutItem] = useState<StorefrontItem | null>(null);
  const [isProcessingCheckout, setIsProcessingCheckout] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);

  useEffect(() => {
    storefrontApi
      .listItems()
      .then((data) => setItems(data))
      .catch((err) => console.warn('Failed to fetch storefront items:', err))
      .finally(() => setIsLoading(false));
  }, []);

  const handleCheckout = (item: StorefrontItem) => {
    setCheckoutItem(item);
    setOrderComplete(false);
  };

  const handlePayNow = async () => {
    if (!checkoutItem) return;
    setIsProcessingCheckout(true);
    try {
      const res = await storefrontApi.initiateCheckout(checkoutItem.id);
      if (res?.checkout_url && res.checkout_url !== '#') {
        window.location.href = res.checkout_url;
      } else {
        setOrderComplete(true);
        setTimeout(() => {
          setCheckoutItem(null);
          setOrderComplete(false);
        }, 2500);
      }
    } catch (err: any) {
      console.error('Checkout error:', err);
      alert(err?.message || 'Unable to initiate checkout. Please try again or verify gateway configuration.');
    } finally {
      setIsProcessingCheckout(false);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto flex flex-col gap-6">
      <StorefrontCatalogView items={items} onCheckout={handleCheckout} />

      {/* Checkout Modal / Drawer */}
      {checkoutItem && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-xl max-w-md w-full p-6 shadow-2xl flex flex-col gap-4">
            <div className="flex items-center justify-between border-b border-border pb-3">
              <div className="flex items-center gap-2">
                <ShoppingBag className="h-5 w-5 text-primary" />
                <h3 className="text-base font-semibold text-foreground">Secure Checkout</h3>
              </div>
              <button
                type="button"
                onClick={() => setCheckoutItem(null)}
                className="text-muted-foreground hover:text-foreground p-1"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {orderComplete ? (
              <div className="text-center py-6 flex flex-col items-center gap-2">
                <CheckCircle className="h-12 w-12 text-emerald-500 animate-bounce" />
                <h4 className="text-lg font-bold text-foreground">Enrollment & Order Confirmed!</h4>
                <p className="text-xs text-muted-foreground">
                  Receipt and course access credentials dispatched via SMS and Email.
                </p>
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                <div>
                  <h4 className="font-semibold text-sm text-foreground">{checkoutItem.name}</h4>
                  <p className="text-xs text-muted-foreground mt-1">{checkoutItem.description}</p>
                </div>

                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border border-border">
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Total Due:</span>
                  <span className="text-lg font-bold text-foreground">
                    {formatMinorUnits(checkoutItem.amountMinor, checkoutItem.currency)}
                  </span>
                </div>

                <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                  <ShieldCheck className="h-3.5 w-3.5 text-accent" />
                  <span>256-Bit SSL Encrypted Payment Bridge with Instant SIS Allocation</span>
                </div>

                <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
                  <button
                    type="button"
                    onClick={() => setCheckoutItem(null)}
                    className="px-4 py-2 rounded-md border border-border hover:bg-muted text-sm font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={isProcessingCheckout}
                    onClick={handlePayNow}
                    className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity min-h-[44px] disabled:opacity-50"
                  >
                    {isProcessingCheckout && <Loader2 className="h-4 w-4 animate-spin" />}
                    <span>Pay Now ({formatMinorUnits(checkoutItem.amountMinor, checkoutItem.currency)})</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export const handle = { crumb: 'Storefront & Checkout' };
